// Pipeline stages
// Funnel order: prospecção → qualificação → reunião → proposta →
// negociação → contrato → ganho/perdido (ganho = won, perdido = lost).
export type PipelineStage =
  | 'prospeccao' | 'qualificacao' | 'reuniao' | 'proposta'
  | 'negociacao' | 'contrato' | 'ganho' | 'perdido';

export const PIPELINE_STAGES: PipelineStage[] = [
  'prospeccao','qualificacao','reuniao','proposta','negociacao','contrato','ganho','perdido',
];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  prospeccao:   'Prospecção',
  qualificacao: 'Qualificação',
  reuniao:      'Reunião',
  proposta:     'Proposta',
  negociacao:   'Negociação',
  contrato:     'Contrato',
  ganho:        'Ganho',
  perdido:      'Perdido',
};

export const STAGE_COLORS: Record<PipelineStage, string> = {
  prospeccao:   '#8c8c8c',
  qualificacao: '#6366F1',
  reuniao:      '#722ed1',
  proposta:     '#fa8c16',
  negociacao:   '#13c2c2',
  contrato:     '#a0d911',
  ganho:        '#52c41a',
  perdido:      '#ff4d4f',
};

export const STAGE_PROBABILITY: Record<PipelineStage, number> = {
  prospeccao: 10, qualificacao: 20, reuniao: 40,
  proposta: 70, negociacao: 80, contrato: 90, ganho: 100, perdido: 0,
};

// Prospect
export interface Prospect {
  id: string;
  tenantId: string;
  companyCoreId?: string;
  nomeEmpresa: string;
  razaoSocial?: string;
  website?: string;
  linkedinUrl?: string;
  cnpj?: string;
  cnae?: string;
  nomeFantasia?: string;
  porte?: string;
  emailEmpresa?: string;
  telefone?: string;
  dataAbertura?: string;
  capitalSocial?: string;
  naturezaJuridica?: string;
  situacaoCadastral?: string;
  tipoEmpresa?: string;
  logradouro?: string;
  numeroEndereco?: string;
  bairro?: string;
  cep?: string;
  complementoEndereco?: string;
  uf?: string;
  simplesOptante?: boolean;
  simeiOptante?: boolean;
  qsa?: Array<{ nome: string; qual: string }>;
  atividadesSecundarias?: Array<{ code: string; text: string }>;
  faturamento?: string;
  funcionarios?: string;
  localizacao?: string;
  techStack?: string;
  scoreCompra?: number;
  scoreEstrategico?: number;
  segmento?: string;
  origemLead?: string;
  /**
   * Categorical provenance of the lead, set by the Radar 360 enrichment engine.
   * - `radar` → surfaced by the Radar recommendations (monthly)
   * - `busca` → created from a Company Discovery search
   * - `importado` → bulk-imported from a base/CSV
   * Distinct from the free-text `origemLead`. Maps from sales-api `lead_source`.
   */
  leadSource?: 'radar' | 'busca' | 'importado' | string;
  observacoes?: string;
  resumoFinanceiro?: string;
  origemDados?: string;
  dataPrimeiroContato?: string;
  dataUltimaInteracao?: string;
  veredicto: 'pendente' | 'em_analise' | 'aprovado' | 'reprovado';
  /** Rejection reason — persisted by janus-sales-api as ``motivo_recusa`` when
   *  the lead is reproved (veredicto='reprovado'). */
  motivoRecusa?: string;
  ownerUserId?: number;
  ownerAssignedAt?: string;
  signals?: unknown;
  /**
   * Set by the backend when the prospect is successfully promoted to a
   * pipeline deal via convert-to-deal. NULL/undefined means the prospect
   * has NOT been converted, regardless of its veredicto value.
   */
  convertedToDealAt?: string | null;
  /**
   * Computed flag from janus-sales-api: True once the async AI enrichment
   * pipeline has populated the minimum set of fields (razao_social + QSA)
   * needed to open the Dados Corporativos tab. Polled by ProspectForm's
   * "AI Enrichment" loading UX.
   */
  enrichmentReady?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export type ProspectVeredicto = Prospect['veredicto'];

export const VEREDICTO_LABELS: Record<ProspectVeredicto, string> = {
  pendente: 'Pendente',
  em_analise: 'Em Analise',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
};

export const VEREDICTO_COLORS: Record<ProspectVeredicto, string> = {
  pendente: 'default',
  em_analise: 'processing',
  aprovado: 'success',
  reprovado: 'error',
};

// Deal
export interface Deal {
  id: string;
  tenantId: string;
  companyCoreId?: string;
  nomeEmpresa: string;
  razaoSocial?: string;
  website?: string;
  linkedinUrl?: string;
  cnpj?: string;
  cnae?: string;
  nomeFantasia?: string;
  porte?: string;
  emailEmpresa?: string;
  telefone?: string;
  dataAbertura?: string;
  capitalSocial?: string;
  naturezaJuridica?: string;
  situacaoCadastral?: string;
  tipoEmpresa?: string;
  logradouro?: string;
  numeroEndereco?: string;
  bairro?: string;
  cep?: string;
  complementoEndereco?: string;
  uf?: string;
  simplesOptante?: boolean;
  simeiOptante?: boolean;
  qsa?: Array<{ nome: string; qual: string }>;
  atividadesSecundarias?: Array<{ code: string; text: string }>;
  faturamento?: string;
  funcionarios?: string;
  localizacao?: string;
  techStack?: string;
  ganchoBdr?: string;
  resumoFinanceiro?: string;
  origemDados?: string;
  segmento?: string;
  /**
   * A etiqueta de PRIMEIRO TOQUE (`deals.origem`, String(120)) — o campo que o
   * filtro `origem` de `GET /pipeline` compara por igualdade exata
   * (`Deal.origem == origem`, `pipeline_service.py`), e que o serializador da
   * lista já emitia sem este tipo o declarar.
   *
   * NÃO confundir com `origemLead`/`origemDados`, que são OUTRAS colunas — o
   * BFF chega a fundir as duas na leitura (`origemLead: d.origemDados ||
   * d.origemLead`). Derivar as opções de um filtro a partir delas ofereceria
   * um dropdown em que quase toda escolha devolve zero; é exatamente por isso
   * que `lead_source` ficou FORA do catálogo de filtros do Autopilot.
   */
  origem?: string;
  origemLead?: string;
  stage: PipelineStage;
  valorEstimado?: number;
  probabilidade?: number;
  responsavel?: string;
  observacoes?: string;
  scoreFinal?: number;
  scoreDetalhes?: Record<string, number>;
  dataPrimeiroContato?: string;
  dataUltimaInteracao?: string;
  motivoPerda?: string;
  ownerUserId?: number;
  ownerAssignedAt?: string;
  executiveUserId?: number;
  executiveAssignedAt?: string;
  /** "+SDR AI" — deal is eligible for the SDR voice-agent campaign pool. */
  sdrCampaignReady?: boolean;
  /** Marca de TESTE INTERNO (sales-api migração 197, decisão 9-A): fora das
   *  métricas do agente, do e-mail de escalada e da cobrança de envio do
   *  agente. Ausente = backend anterior (tratado como não marcado). */
  isInternalTest?: boolean;
  /** B7 — link deal→Conta Comercial (auto-link por CNPJ ou late-binding). */
  commercialAccountId?: string | null;
  /** Janus Origin — REF curto (JNS-XXXXXXXX) e tag p/ colar no CRM externo. */
  janusOriginRef?: string;
  janusOriginTag?: string;
  nomeOportunidade?: string | null;
  dataPrevistaFechamento?: string | null;
  tipoNegocio?: 'Novo Negócio' | 'Renovação' | 'Expansão' | 'novo_negocio' | 'renovacao' | 'expansao' | string | null;
  proximoPasso?: string | null;
  criteriosBant?: { budget: boolean; authority: boolean; need: boolean; timeline: boolean } | null;
  // ── LinkedIn / rich company data (optional, populated by backfill; may be null/undefined) ──
  /** Company logo URL (may expire → needs a fallback chain in CompanyLogo). */
  logoUrl?: string;
  /** LinkedIn follower count. */
  followerCount?: number;
  /** Numeric headcount — distinct from the existing free-text `funcionarios`. */
  employeeCount?: number;
  /** Headcount bracket (e.g. 501–1.000). */
  employeeCountRange?: { start: number; end: number };
  /** Industries (e.g. ["Automotive"]). */
  industries?: string[];
  /** Specialities (e.g. ["montadora nacional"]). */
  specialities?: string[];
  /** Office locations; the headquarter flag marks the HQ row. */
  locations?: Array<{
    city?: string;
    geographicArea?: string;
    country?: string;
    line1?: string;
    postalCode?: string;
    headquarter?: boolean;
  }>;
  /** Company type (e.g. "Privately Held"). */
  companyType?: string;
  /** LinkedIn company description — separate from the `descricao` business narrative. */
  linkedinDescription?: string;
  createdAt: string;
  updatedAt: string;
  signals?: unknown;
  contacts?: Contact[];
  activities?: Activity[];
  tags?: DealTag[];
}

export interface DealTag {
  id: string;
  tenantId: string;
  name: string;
  color: string;
  description?: string | null;
  dealCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DealTagCreateRequest {
  name: string;
  color: string;
  description?: string | null;
}

export interface DealTagUpdateRequest {
  name?: string;
  color?: string;
  description?: string | null;
}

// Contact
export interface Contact {
  isLeadership?: boolean;
  discoverySources?: string[];
  discoveryQuality?: {
    /** Prévia descoberta, sem conceder revelação ou acesso aos canais. */
    previewOnly?: boolean;
    placeholder?: boolean;
    emailDomainMismatch?: boolean;
    reasons?: string[];
  };
  id: string;
  contactCoreId?: string | null;
  unlockedAt?: string | null;
  dealId: string;
  tenantId: string;
  nome: string;
  cargo?: string;
  /** pt-BR-normalized job title; falls back to raw `cargo`. */
  cargoPt?: string;
  email?: string;
  telefone?: string;
  /**
   * All known emails/phones for this contact (a contact can have 2+ of each).
   * `email`/`telefone` above remain the primary (first) value. Arrays may be
   * undefined on older API responses — callers fall back to the single value.
   */
  emails?: string[];
  phones?: string[];
  linkedinUrl?: string;
  eDecisor: boolean;
  icpMatch?: boolean;
  /**
   * Proveniência de indicação (spec 07): `origin='referral'` marca contato
   * cadastrado a partir de uma indicação na conversa (cartão/telefone/e-mail
   * compartilhado), e `referredByFirstName` diz QUEM indicou — o badge
   * "Indicado por {nome}" da aba Contatos lê daqui.
   */
  origin?: string;
  referredByFirstName?: string;
  referredByContactId?: string;
  observacoes?: string;
  hasContactData?: boolean;
  seniorityJanus?: string;
  departmentJanus?: string;
  whatsapp_invalido?: boolean;
  waba_bloqueado?: boolean;
  whatsappInvalido?: boolean;
  wabaBloqueado?: boolean;
  billingStatus?: string | null;
  billing_status?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ActivityStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
export type ActivityPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Activity {
  id: string;
  tenantId: string;
  dealId?: string | null;
  prospectId?: string | null;
  contactId?: string | null;
  commercialAccountId?: string | null;
  title?: string;
  tipo:
    | 'ligacao'
    | 'email'
    | 'reuniao'
    | 'nota'
    | 'status_change'
    | 'enrichment'
    | 'scoring'
    | 'linkedin_conexao'
    | 'linkedin_mensagem'
    | 'whatsapp'
    | 'tarefa';
  status: ActivityStatus;
  priority: ActivityPriority;
  dueAt?: string | null;
  durationMinutes?: number | null;
  descricao?: string | null;
  /** Ex.: `fu-block:{conversa}:{tipo}` no aviso de follow-up bloqueado (E5c). */
  externalRef?: string | null;
  metadata?: Record<string, unknown>;
  autor?: string;
  assignedToUserId?: number | null;
  completedAt?: string | null;
  completedByUserId?: number | null;
  outcome?: string | null;
  outcomeNotes?: string | null;
  actionType?: string;
  actionPayload?: Record<string, unknown> | null;
  actionStatus?: string;
  actionExecutedAt?: string | null;
  reminderAt?: string | null;
  reminderMinutesBefore?: number[] | null;
  reminderChannels?: string[] | null;
  dealName?: string | null;
  contactName?: string | null;
  accountName?: string | null;
  autopilotAviso?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface ActivitySummary {
  overdue: number;
  today: number;
  upcoming: number;
  completedToday: number;
  totalPending: number;
}

export type ActivityType = Activity['tipo'];

/** Autores que o motor de cadência do Autopilot usa ao escrever uma
 *  Activity automaticamente — espelha SÓ o subconjunto "Autopilot" de
 *  `AUTORES_DE_MAQUINA` (sales-api `autopilot_manual_takeover.py`), que é
 *  mais largo (inclui marketing/telefonia/agente de mensageria/"sistema"
 *  genérico) porque serve a um propósito diferente (decidir se um toque
 *  desliga a automação). O selo da timeline é só sobre a cadência. */
export const AUTOPILOT_ACTIVITY_AUTORES = new Set(['autosend', 'cadence']);

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  ligacao: 'Ligação',
  email: 'E-mail',
  reuniao: 'Reunião',
  nota: 'Nota',
  status_change: 'Mudança de Status',
  enrichment: 'Enriquecimento',
  scoring: 'Scoring',
  linkedin_conexao: 'Conexão LinkedIn',
  linkedin_mensagem: 'Mensagem LinkedIn',
  whatsapp: 'WhatsApp',
  tarefa: 'Tarefa',
};

export const ACTIVITY_TYPE_COLORS: Record<ActivityType, string> = {
  ligacao: 'blue',
  email: 'green',
  reuniao: 'purple',
  nota: 'default',
  status_change: 'orange',
  enrichment: 'cyan',
  scoring: 'gold',
  linkedin_conexao: 'geekblue',
  linkedin_mensagem: 'geekblue',
  whatsapp: 'success',
  tarefa: 'volcano',
};

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  cancelled: 'Cancelada',
};

export const ACTIVITY_STATUS_COLORS: Record<ActivityStatus, string> = {
  pending: 'processing',
  in_progress: 'warning',
  completed: 'success',
  cancelled: 'default',
};

export const ACTIVITY_PRIORITY_LABELS: Record<ActivityPriority, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  urgent: 'Urgente',
};

export const ACTIVITY_PRIORITY_COLORS: Record<ActivityPriority, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
};

// Alert
//
// ── O VOCABULARIO DAQUI ESTAVA ERRADO, E A TELA MOSTRAVA TAG VAZIA ──────────
//
// Medido nos tres hops em 2026-09-06 (a migracao de `/alerts` para o modelo de
// rail e' o AUDIT dos filtros, e foi ela que encontrou isto):
//
//   janus-sales-api  `AlertSeverity` = critical | warning | info
//                    `AlertType`     = estagnacao | followup_vencido | meta_em_risco
//   janus-bff        passthrough puro (`toCamelCase` converte CHAVE, nunca VALOR)
//   janus-frontend   declarava low|medium|high|critical e stagnation|risk|opportunity
//
// `tipo` nao tinha UM valor em comum; `severidade` so' `critical`. E como as
// duas superficies (`/alerts` e o card "Alertas Recentes" do dashboard) fazem
// `SEVERITY_LABELS[alert.severidade]`, um alerta `warning` — o mais comum da
// plataforma, porque `generate_alerts_batch` so' cria `estagnacao` com
// `warning` ou `critical` — renderizava uma **tag de gravidade SEM TEXTO** e o
// tipo saia como o slug cru `estagnacao`.
//
// O backend e' a verdade (o enum + as linhas na tabela); o tipo daqui e' que
// mentia. Por isso a correcao e' so'-frontend.
export interface Alert {
  id: string;
  tenantId: string;
  dealId: string;
  tipo: AlertType;
  severidade: AlertSeverity;
  mensagem: string;
  lida: boolean;
  metadata?: Record<string, unknown>;
  createdAt: string;
  /** Company name + responsável, resolved server-side (janus-sales-api joins
   *  the deal and resolves the owner name) so any consumer gets full context. */
  companyName?: string;
  responsavel?: string;
}

/** Espelha `AlertSeverity` do janus-sales-api (`app/models/alert.py`). */
export type AlertSeverity = 'critical' | 'warning' | 'info';

/** Espelha `AlertType` do janus-sales-api (`app/models/alert.py`). */
export type AlertType = 'estagnacao' | 'followup_vencido' | 'meta_em_risco';

export const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: 'red',
  warning: 'orange',
  info: 'blue',
};

export const SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: 'Crítica',
  warning: 'Atenção',
  info: 'Informativa',
};

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  estagnacao: 'Estagnação',
  followup_vencido: 'Follow-up vencido',
  meta_em_risco: 'Meta em risco',
};

export const ALERT_TYPE_COLORS: Record<AlertType, string> = {
  estagnacao: 'orange',
  followup_vencido: 'red',
  meta_em_risco: 'purple',
};

/**
 * O rotulo de um valor que o backend pode passar a emitir amanha.
 *
 * **Valor desconhecido volta CRU, nunca vazio nem inventado.** E' a mesma regra
 * do `meta_error_catalog` (sigla sem verbete aparece como veio) — e e'
 * literalmente o defeito que esta entrega fecha: `SEVERITY_LABELS[x]` com `x`
 * fora do mapa devolve `undefined`, e `<Tag>{undefined}</Tag>` desenha uma tag
 * sem texto, que nao informa nem denuncia. Um slug feio na tela e' pior que um
 * rotulo bonito, e melhor que nada.
 */
export function rotuloDeGravidade(v: string): string {
  return SEVERITY_LABELS[v as AlertSeverity] ?? v;
}

export function corDeGravidade(v: string): string | undefined {
  return SEVERITY_COLORS[v as AlertSeverity];
}

export function rotuloDeTipoDeAlerta(v: string): string {
  return ALERT_TYPE_LABELS[v as AlertType] ?? v;
}

export function corDeTipoDeAlerta(v: string): string | undefined {
  return ALERT_TYPE_COLORS[v as AlertType];
}

// Scoring
export interface ScoreResult {
  dealId: string;
  scoreFinal: number;
  detalhes: {
    dadosLead: number;
    triggerEvents: number;
    intelBdr: number;
    timing: number;
  };
  classificacao: string;
}

// Forecast
export interface ForecastStageDetail {
  count: number;
  valorBruto?: number;
  valor_bruto?: number;
  forecast: number;
  prob: number;
}

export interface ForecastResponse {
  totalPipeline?: number;
  forecastPonderado?: number;
  pipelineBruto?: number;
  pipeline_bruto?: number;
  forecastTotal?: number;
  forecast_total?: number;
  nDeals?: number;
  porEtapa?: Record<string, ForecastStageDetail>;
  por_etapa?: Record<string, ForecastStageDetail>;
  byStage?: Array<{
    stage: string;
    count: number;
    totalValor: number;
    mediaProbabilidade?: number;
    prob?: number;
    forecastPonderado: number;
  }>;
}

// Paginated response
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

// Auth
//
// Wave 3 RBAC sanitization: keeps the legacy 'admin' string for back-compat
// with anything that still consumes MANAGER_ROLES, but adds 'tenant_admin'
// (the canonical replacement) so canonical user objects also match. Prefer
// the helpers in src/shared/rbac/roles.ts (MANAGER_ROLES set + hasAnyRole)
// for any new code path.
export const MANAGER_ROLES = [
  'admin', // legacy alias
  'account_owner', // tenant owner — admin-tier, ranks above tenant_admin
  'tenant_admin',
  'diretor',
  'gestor',
  'super_admin',
  'master_admin',
] as const;
export type ManagerRole = typeof MANAGER_ROLES[number];

/** Type-safe check for manager roles, avoids `as any` casts */
export function isManagerRole(role: string): role is ManagerRole {
  return (MANAGER_ROLES as readonly string[]).includes(role);
}

export interface User {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  role: string;
  /** Optional canonical role list (Wave 3); kept loose to avoid coupling. */
  roles?: string[];
  modules?: string[];
  // Profile fields (may be present after name split on backend)
  firstName?: string;
  lastName?: string;
  // Restricted JWT (must change password)
  restricted?: boolean;
  // WhatsApp integration fields
  phoneE164?: string;
  whatsappDeviceAuthorized?: boolean;
  // Onboarding fields
  firstLogin?: boolean;
  onboardingCompletedAt?: string | null;
  onboardingInterviewStatus?: 'pending' | 'in_progress' | 'paused' | 'completed';
  tenantActivatedAt?: string | null;
  /**
   * Email signature persisted server-side (cross-device) for the rich email
   * composer. Snake_case `email_signature` from the backend may also be
   * present when the response isn't camelCased — read both at the call site.
   */
  emailSignature?: EmailSignatureData | null;
  email_signature?: EmailSignatureData | null;
  /**
   * Avatar URL — the LinkedIn photo persisted on the user (janus-auth /me).
   * Google/Microsoft photos are fetched live via `useProfilePhoto()`.
   */
  profilePhotoUrl?: string | null;
}

/** Persisted email signature shape (mirrors janus-auth users.email_signature). */
export interface EmailSignatureData {
  name?: string;
  role?: string;
  phone?: string;
  company?: string;
  website?: string;
  /** Optional pre-rendered HTML; when absent the composer renders the fields. */
  html?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  user: User;
  /** Set when the user must change their password on first login */
  must_change_password?: boolean;
  /** Set when the JWT is restricted (change-password flow) */
  restricted?: boolean;
}

// API error
export interface ApiError {
  detail?: string;
  message: string;
  statusCode: number;
  error?: string;
}

// ─── Intelligence types ────────────────────────────────────────────

export interface MarketListRequest {
  sector: string;
  location: string;
  limit?: number;
  provider?: string;
}

export interface MarketCompany {
  name: string;
  sector: string;
  location: string;
  website?: string;
  linkedinUrl?: string;
  description?: string;
  employeeCount?: string;
  annualRevenue?: string;
  cnpj?: string;
  razaoSocial?: string;
  score?: number;
  verdict?: string;
  techStack?: string;
  decisionMaker?: string;
  triggerEvent?: string;
  approachHook?: string;
  maturity?: string;
  strategicFocus?: string;
}

export interface MarketListResponse {
  companies: MarketCompany[];
  totalFound: number;
  sector: string;
  location: string;
}

export interface BdrHookRequest {
  companyName: string;
  sector?: string;
  context?: string;
  provider?: string;
}

export interface BdrHookResponse {
  hook: string;
  rationale?: string;
}

export interface ColdEmailRequest {
  companyName: string;
  contactName?: string;
  sector?: string;
  context?: string;
  provider?: string;
}

export interface ColdEmailResponse {
  subject: string;
  body: string;
}

export interface NewsMonitorRequest {
  companyName: string;
  limit?: number;
  provider?: string;
}

export interface NewsItem {
  title: string;
  summary: string;
  date: string;
  source?: string;
  /** 0..1 relevance from machine-ai's grounded news-monitor. */
  relevanceScore?: number;
  /** Real article URL. machine-ai sends `source_url` → BFF camelCases it to
   *  `sourceUrl`; `url` kept as a back-compat alias. */
  sourceUrl?: string;
  url?: string;
  /** Legacy string relevance (kept optional for back-compat). */
  relevance?: string;
}

export interface NewsMonitorResponse {
  news: NewsItem[];
  companyName: string;
}

export interface FinancialContextRequest {
  companyName: string;
  ticker?: string;
  analysisType?: string;
  provider?: string;
}

export interface FinancialContextResponse {
  context: string;
  keyMetrics?: Record<string, string>;
  summary?: string;
}

export interface WinLossRequest {
  dealId: string;
  outcome: 'ganho' | 'perdido';
  reason?: string;
  dealContext?: Record<string, unknown>;
  provider?: string;
}

export interface WinLossResponse {
  analysis: string;
  keyFactors: string[];
  recommendations: string[];
}

// ─── Visão 360 types ───────────────────────────────────────────────────

export interface Visao360ProductFit {
  product: string;
  score: number;
  rationale: string;
  keyFeatures: string[];
}

export interface Visao360Objection {
  objection: string;
  response: string;
}

export interface Visao360CallToAction {
  type?: string;
  description?: string;
}

export interface Visao360Approach {
  strategy: string;
  openingHook: string;
  valueProposition: string;
  keyPainPoints: string[];
  differentiators: string[];
  discoveryQuestions: string[];
  objectionHandling: Visao360Objection[];
  // Schema migrated from string to {type, description} in the visao_360
  // prompt overhaul. Accept both for backwards compat with payloads
  // produced before the prompt cutover.
  callToAction: string | Visao360CallToAction;
}

export interface Visao360Analysis {
  executiveSummary: string;
  approach: Visao360Approach;
  productFit: Visao360ProductFit[];
  riskFactors: string[];
  recommendedNextSteps: string[];
  parsingFailed?: boolean;
}

// ─── Admin types ────────────────────────────────────────────────────────

export interface CreateUserPayload {
  email: string;
  password: string;
  name: string;
  role?: string;
  roles?: string[];
  tenantId?: string;
}

export interface UpdateUserPayload {
  name?: string;
  role?: string;
  roles?: string[];
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  roles?: string[];
  active: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt?: string;
}

// ─── Quote-to-Cash foundations (SF-ACC-001 / SF-PROD-001) ──────────────────

/** Tax regime shared by commercial accounts, products and the tax profile. */
export type RegimeTributario = 'simples' | 'presumido' | 'real';

/** SF-ACC-001 — Conta Comercial (commercial account / billing entity). */
export interface CommercialAccount {
  id: string;
  accountId: string;
  companyCoreId?: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj?: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
  regimeTributario?: RegimeTributario;
  segmento?: string;
  porte?: string;
  site?: string;
  /**
   * Origin CRM system this account was loaded/synced from (e.g.
   * `oracle_fusion` for the Oracle/Finnet migration). NULL = created natively
   * in Janus. Surfaced as the "Origem" badge on the Contas Comerciais list.
   */
  crmSourceSystem?: string | null;
  /** Buyer id in the client's own/origin CRM (paired with `crmSourceSystem`). */
  externalCrmId?: string | null;
  status: 'prospect' | 'cliente' | 'inativo';
  ownerUserId?: number;
  parentCommercialAccountId?: string | null;
  dadosJson?: Record<string, unknown> | null;
  /**
   * Base instalada (produtos contratados) — contagem em lote devolvida pela
   * listagem. Diferencia cliente real (assetsCount/ordersCount > 0) de
   * prospect/topo-de-funil. Ausente em respostas antigas do BFF.
   */
  assetsCount?: number | null;
  ordersCount?: number | null;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CommercialAccountStatus = CommercialAccount['status'];

/** Lightweight deal row returned by the account 360 view. */
export interface CommercialAccount360Deal {
  id: string;
  nomeEmpresa: string;
  stage: string;
}

/**
 * B8 — per-Conta aggregated support-health signal (from Oracle serviceRequests).
 * Aggregate only (LGPD): counts + ranks + a computed verdict, no ticket text.
 * `null` on the 360 payload until the first sr-signal-refresh runs.
 */
export interface CommercialAccountSupportSignal {
  ticketsTotal: number;
  tickets90d: number;
  ticketsAbertos: number;
  criticos12m: number;
  severidadeMedia: number | null;
  pctResolvidoNoSla: number | null;
  ultimaAtividade: string | null;
  supportHealthScore: number;
  trafficLight: 'verde' | 'amarelo' | 'vermelho';
  atRisk: boolean;
  refreshedAt: string | null;
}

/** Response of GET /commercial-accounts/:id/360. */
export interface CommercialAccount360 {
  account: CommercialAccount;
  counts: { deals: number; contacts: number; assets?: number; orders?: number };
  deals: CommercialAccount360Deal[];
  proposals: unknown[];
  contracts: unknown[];
  /** B8 — "Saúde de suporte" card data; null until first refresh. */
  supportSignal?: CommercialAccountSupportSignal | null;
}

/**
 * SF-ASSET-001 — Ativo / Installed Base item: an active product or
 * subscription owned by a Conta Comercial. Read-only on the FE; created
 * server-side when a contract/deal closes. The BFF camelCases the sales-api
 * snake_case payload before it reaches us.
 */
export type AssetStatus = 'ativo' | 'expirado' | 'suspenso' | 'cancelado';

export interface Asset {
  id: string;
  commercialAccountId: string;
  productId?: string | null;
  nome: string;
  sku?: string | null;
  quantidade: number;
  precoUnitario: number;
  recorrencia?: string | null;
  valorTotal: number;
  status: AssetStatus;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  createdAt: string;
}

/** GET/PUT /document-identity — the tenant's own billing identity. */
export interface DocumentIdentity {
  id: string;
  accountId: string;
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj?: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  cep?: string;
  logoUrl?: string;
  corPrimaria?: string;
  signatarioPadraoNome?: string;
  signatarioPadraoCargo?: string;
  signatarioPadraoEmail?: string;
  condicoesPadrao?: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** SF-PROD-001 — catalog product / service / subscription. */
export interface AccountProduct {
  id: string;
  accountId: string;
  nome: string;
  descricao?: string;
  sku?: string;
  tipo: 'produto' | 'servico' | 'assinatura';
  unidade?: string;
  recorrencia: 'unico' | 'mensal' | 'anual';
  precoBase: number;
  moeda: string;
  /** NULL when the caller lacks the manage permission (cost is masked). */
  custo?: number | null;
  /** Nullable/masked together with `custo`. */
  margemMinimaPct?: number | null;
  regimeTributario?: RegimeTributario;
  precoIncluiImposto: boolean;
  issPct?: number | null;
  icmsPct?: number | null;
  codigoServico?: string;
  ncm?: string;
  ativo: boolean;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AccountProductTipo = AccountProduct['tipo'];
export type AccountProductRecorrencia = AccountProduct['recorrencia'];

/** A price book (table of prices) within the account. */
export interface AccountPriceBook {
  id: string;
  accountId: string;
  nome: string;
  isPadrao: boolean;
  moeda: string;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A single product price entry inside a price book. */
export interface PriceBookEntry {
  id: string;
  accountId: string;
  priceBookId: string;
  productId: string;
  preco: number;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** GET/PUT /tax-profile — the account's default fiscal configuration. */
export interface TaxProfile {
  id: string;
  accountId: string;
  regimeTributario: RegimeTributario;
  municipioIss?: string;
  issPadraoPct?: number | null;
  icmsPadraoPct?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /products/:id/pricing — resolved pricing for a product (+ price book). */
export interface ProductPricing {
  productId: string;
  priceBookId?: string;
  tipo: string;
  moeda: string;
  preco: number;
  precoIncluiImposto: boolean;
  aliquotaAplicadaPct: number;
  precoLiquido: number;
  precoBruto: number;
  custo?: number | null;
  margemMinimaPct?: number | null;
  precoMinimoPermitido?: number | null;
  abaixoDoPiso?: boolean | null;
}

// ─── Proposta / CPQ (SF-CPQ-001) ───────────────────────────────────────────

/** State machine of a proposal (Quote) — see PROPOSAL_SPEC §3.2. */
export type ProposalStatus =
  | 'rascunho'
  | 'em_aprovacao'
  | 'aprovada_interna'
  | 'enviada'
  | 'aceita'
  | 'recusada'
  | 'expirada'
  | 'cancelada';

/** Recurrence of a proposal line item (mirrors AccountProductRecorrencia). */
export type ProposalRecorrencia = 'unico' | 'mensal' | 'anual';

/** A single line item (QuoteLineItem) inside a proposal. */
export interface ProposalLineItem {
  id: string;
  proposalId: string;
  productId?: string | null;
  descricao?: string;
  quantidade: number;
  precoUnitario: number;
  descontoPct: number;
  totalLinha: number;
  recorrencia?: ProposalRecorrencia | string;
  ordem: number;
}

/** SF-CPQ-001 — Proposta (Quote), anchored to a deal. */
export interface Proposal {
  id: string;
  dealId: string;
  numero: string;
  nome?: string;
  status: ProposalStatus;
  versao: number;
  priceBookId?: string | null;
  subtotal: number;
  descontoPct: number;
  descontoValor: number;
  total: number;
  /**
   * Totais por recorrência (DERIVADOS das linhas, backend janus-revenue). Não
   * substituem `total` (que soma tudo) — classificam o que é pagamento na
   * contratação uma única vez (`totalSetup`) do que é recorrente
   * (`totalRecorrente` = mensal + anual). `undefined` em propostas anteriores à
   * classificação (o front trata como 0).
   */
  totalSetup?: number;
  totalMensal?: number;
  totalAnual?: number;
  totalRecorrente?: number;
  moeda: string;
  validadeAte?: string | null;
  condicoesPagamento?: string | null;
  observacoes?: string | null;
  escopo?: string | null;
  escopoHtml?: string | null;
  /**
   * Internal sales negotiation strategy (markdown). MANAGERS-ONLY — sales-api
   * stores it in `notas_internas` and excludes it from the client-facing PDF /
   * public accept view; the BFF strips it from `GET /proposals/:id` for
   * non-manager callers. NEVER render this in a client-facing surface.
   */
  notasInternas?: string | null;
  templateId?: string | null;
  documentoUrl?: string | null;
  documentoGeradoEm?: string | null;
  ownerUserId?: number;
  enviadaEm?: string | null;
  enviadaParaContactId?: string | null;
  /**
   * Todos os contatos escolhidos para o aceite/envio (decisão do dono
   * 2026-09-03 — envio para vários). O singular `enviadaParaContactId` é o
   * primeiro. `undefined`/`[]` = proposta anterior à migração 0015 (cai no
   * singular).
   */
  enviadaParaContactIds?: string[] | null;
  /**
   * Desfecho REAL do e-mail — distinto de `status: 'enviada'`, que só diz que
   * o operador despachou. Em 2026-08-25 a PROP-2026-0039 exibia "Enviada" com
   * `EMAIL_ENABLED=false` no backend: nenhum e-mail saiu e a tela não dizia.
   * `undefined`/`null` = proposta anterior à migração 0014 (desconhecido, que
   * NÃO é o mesmo que "não enviado").
   */
  emailStatus?: 'enviado' | 'pulado_desabilitado' | 'sem_destinatario' | 'falhou' | null;
  emailEnviadoEm?: string | null;
  emailDestino?: string | null;
  /** Todos os e-mails para os quais o envio saiu (o singular é o primeiro). */
  emailDestinos?: string[] | null;
  emailErro?: string | null;
  aceitaEm?: string | null;
  aceiteIp?: string | null;
  aceiteNome?: string | null;
  lineItems?: ProposalLineItem[];
  createdAt?: string;
  updatedAt?: string;
}

/** Status of a discount approval request (Approval Process). */
export type ProposalApprovalStatus = 'pendente' | 'aprovado' | 'rejeitado';

/** A discount-approval record on a proposal. */
export interface ProposalApproval {
  id: string;
  proposalId: string;
  aprovadorUserId?: number | null;
  status: ProposalApprovalStatus;
  regraDisparada?: string | null;
  motivo?: string | null;
  solicitadoEm?: string | null;
  decididoEm?: string | null;
}

/** An audit-trail event in the proposal lifecycle (append-only). */
export interface ProposalEvent {
  id: string;
  proposalId: string;
  evento: string;
  atorUserId?: number | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

/** A reusable proposal document template (Quote Template). */
export interface ProposalTemplate {
  id: string;
  nome: string;
  corpoHtml?: string;
  cabecalho?: string;
  rodape?: string;
  logoUrl?: string;
  isPadrao: boolean;
  ativo: boolean;
  variaveis?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Result of POST /proposals/:id/generate-document — the freshly rendered
 * (versioned) document and its signed URL.
 */
export interface ProposalDocument {
  proposalId: string;
  versao: number;
  documentoUrl: string;
  documentoGeradoEm: string;
  contentType: string;
}

/**
 * List envelope for proposals. The BFF returns `{items, total, limit, offset}`
 * for this surface (distinct from the `{data, ...}` `PaginatedResponse`).
 */
export interface ProposalListResponse {
  items: Proposal[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Public (unauthenticated) view of a proposal, served to the external decisor
 * via the accept-link token. Never includes internal/owner fields.
 */
export interface PublicProposalView {
  numero: string;
  nome?: string;
  status: ProposalStatus;
  nomeEmpresa?: string;
  subtotal: number;
  descontoPct: number;
  descontoValor: number;
  total: number;
  /** Split único × recorrente (derivado) — mesma classificação do documento. */
  totalSetup?: number;
  totalRecorrente?: number;
  moeda: string;
  validadeAte?: string | null;
  condicoesPagamento?: string | null;
  observacoes?: string | null;
  escopo?: string | null;
  escopoHtml?: string | null;
  expirada: boolean;
  lineItems: ProposalLineItem[];
}

// ─── Contrato / CLM (SF-CLM-001) ───────────────────────────────────────────

/** State machine of a contract (CLM) — see CONTRACT_SPEC §3.2. */
export type ContractStatus =
  | 'rascunho'
  | 'enviado'
  | 'parcialmente_assinado'
  | 'assinado'
  | 'vigente'
  | 'encerrado'
  | 'cancelado';

/** Reajuste index applied to the contract value over its term. */
export type ContractIndiceReajuste = 'nenhum' | 'IPCA' | 'IGPM';

/** Role of a signer on a contract. */
export type ContractSignerPapel =
  | 'contratante'
  | 'contratada'
  | 'testemunha'
  | 'avalista';

/** Per-signer signature status. */
export type ContractSignerStatus = 'pendente' | 'assinado' | 'recusado';

/**
 * A signer on a contract. The CPF is NEVER returned in full by the BFF — only
 * the masked form `cpfMascarado` (`***.456.789-**`) is exposed to the client.
 */
export interface ContractSigner {
  id: string;
  contractId: string;
  nome: string;
  email: string;
  cpfMascarado?: string | null;
  papel: ContractSignerPapel | string;
  ordem: number;
  status: ContractSignerStatus | string;
  assinadoEm?: string | null;
  ip?: string | null;
}

/** SF-CLM-001 — Contrato (CLM), generated from an accepted proposal. */
export interface Contract {
  id: string;
  dealId: string;
  proposalId: string;
  commercialAccountId?: string | null;
  numero: string;
  nome?: string;
  status: ContractStatus;
  versao: number;
  valorTotal: number;
  moeda: string;
  vigenciaInicio?: string | null;
  vigenciaFim?: string | null;
  prazoMeses?: number | null;
  renovacaoAutomatica?: boolean;
  indiceReajuste?: ContractIndiceReajuste | string | null;
  documentoUrl?: string | null;
  assinaturaProvider?: string | null;
  assinadoEm?: string | null;
  /**
   * Manual flow (no e-signature provider): signed URL of the proof of
   * acceptance (`comprovante`) attached by the user when finalizing the
   * contract. Present once the contract reaches `vigente` via the manual path.
   */
  comprovanteUrl?: string | null;
  /** Optional note recorded alongside the manual `comprovante`. */
  comprovanteObservacao?: string | null;
  /** Timestamp the contract was marked signed via the manual flow. */
  assinadoManualmenteEm?: string | null;
  condicoes?: string | null;
  observacoes?: string | null;
  signers?: ContractSigner[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Result of POST /contracts/:id/send-email — the manual flow's "email the
 * contract" step. The BFF returns a human-readable confirmation plus the
 * recipient the document was sent to.
 */
export interface ContractSendEmailResult {
  message: string;
  recipientEmail?: string | null;
  sentAt?: string | null;
}

/** An audit-trail event in the contract lifecycle (append-only). */
export interface ContractEvent {
  id: string;
  contractId: string;
  evento: string;
  atorUserId?: number | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * Result of POST /contracts/:id/generate-document — the freshly rendered
 * (versioned) document and its signed URL.
 */
export interface ContractDocument {
  contractId: string;
  versao: number;
  documentoUrl: string;
  contentType: string;
  pdfGenerated: boolean;
}

/**
 * List envelope for contracts. The BFF returns `{items, total, limit, offset}`
 * for this surface (mirrors `ProposalListResponse`).
 */
export interface ContractListResponse {
  items: Contract[];
  total: number;
  limit: number;
  offset: number;
}

// ─── Icon size constants ───────────────────────────────
export const ICON_SIZE_XS = 12 as const;
export const ICON_SIZE_SM = 14 as const;
export const ICON_SIZE_MD = 16 as const;
export const ICON_SIZE_LG = 20 as const;
export const ICON_SIZE_XL = 24 as const;
